import React, { useMemo } from "react";
import { ATTR_LABELS, CATEGORY_ATTR_HIGHLIGHTS } from "../lib/categories";

/**
 * Sidebar filter panel.
 * - Price range (min/max)
 * - Sort
 * - Category-specific attribute filters (client-side)
 *
 * Props:
 *   category, filters, setFilters, sort, setSort,
 *   attrOptions: { [key]: string[] }  (derived from currently loaded products)
 *   attrFilters, setAttrFilters
 */
export default function Filters({
  category,
  filters,
  setFilters,
  sort,
  setSort,
  attrOptions = {},
  attrFilters,
  setAttrFilters,
  onReset,
}) {
  const highlightKeys = useMemo(
    () => CATEGORY_ATTR_HIGHLIGHTS[category] || [],
    [category]
  );

  return (
    <aside
      className="lg:sticky lg:top-32 w-full lg:w-64 shrink-0"
      data-testid="filters-sidebar"
    >
      <div className="border border-neutral-200 rounded-xl p-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-sm uppercase tracking-widest text-neutral-500">
            Filters
          </h3>
          <button
            onClick={onReset}
            className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
            data-testid="filters-reset-button"
          >
            Reset
          </button>
        </div>

        {/* Sort */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-neutral-600 mb-2">
            Sort
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:border-neutral-900 transition-colors"
            data-testid="filter-sort-select"
          >
            <option value="featured">Featured</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {/* Price range */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-neutral-600 mb-2">
            Price ($)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.min_price ?? ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  min_price: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full h-10 rounded-md border border-neutral-300 px-3 text-sm focus:outline-none focus:border-neutral-900"
              data-testid="filter-min-price"
            />
            <span className="text-neutral-400">—</span>
            <input
              type="number"
              placeholder="Max"
              value={filters.max_price ?? ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  max_price: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full h-10 rounded-md border border-neutral-300 px-3 text-sm focus:outline-none focus:border-neutral-900"
              data-testid="filter-max-price"
            />
          </div>
        </div>

        {/* Attribute filters (client-side) */}
        {category &&
          highlightKeys.map((k) => {
            const opts = attrOptions[k];
            if (!opts || opts.length < 2) return null;
            return (
              <div className="mb-5" key={k}>
                <label className="block text-xs font-medium text-neutral-600 mb-2">
                  {ATTR_LABELS[k] || k}
                </label>
                <select
                  value={attrFilters[k] || ""}
                  onChange={(e) =>
                    setAttrFilters({
                      ...attrFilters,
                      [k]: e.target.value || undefined,
                    })
                  }
                  className="w-full h-10 rounded-md border border-neutral-300 px-3 text-sm focus:outline-none focus:border-neutral-900"
                  data-testid={`filter-attr-${k}`}
                >
                  <option value="">Any</option>
                  {opts.map((v) => (
                    <option key={String(v)} value={String(v)}>
                      {String(v)}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
      </div>
    </aside>
  );
}
